"use client";

import { ForwardedRef, forwardRef, useContext } from "react";
import ResponsiveMenuContext from "@/contexts/responsiveMenu.context";
import style from "./ResponsiveMenu.module.scss";
import CloseMenu from "@/assets/svg/close/outline.svg";
import Link from "next/link";
import LoginIcon from "@/assets/svg/login/round.svg";
import { useTranslation } from "@/modules/i18n/i18n.hooks";
import LinkMenu from "./LinkMenu/LinkMenu";
import AppLangChoice from "../AppSetLang/AppLangChoice";
import { FormattedMessage } from "react-intl";
import UserMenu from "../Navbar/UserMenu";
import isUserMenuDisplayedContext from "@/contexts/userMenu.context";
import isTransparentDisplayedContext from "@/contexts/transparentDiv.context";
import OutlineIcon from "@/assets/svg/arrow_drop_down/outline.svg";
import { useGetSession } from "@/modules/auth/auth.hooks";
import { calendlyUrl } from "@/modules/sales/sales.consts";

type ResponsiveMenuProps = {
  props: {};
};

const ResponsiveMenu = forwardRef(
  ({ props }: ResponsiveMenuProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { isResponsiveMenuDisplayed, setIsResponsiveMenuDisplayed } =
      useContext(ResponsiveMenuContext);

    const { me, isUserConnected } = useGetSession();

    const { t } = useTranslation();

    const { isUserMenuDisplayed, setIsUserMenuDisplayed } = useContext(
      isUserMenuDisplayedContext,
    );
    const { isTransparentDivDisplayed, setIsTransparentDivDisplayed } =
      useContext(isTransparentDisplayedContext);

    const handleDisplayUserMenu = () => {
      setIsTransparentDivDisplayed(true);
      setIsUserMenuDisplayed(!isUserMenuDisplayed);
    };

    return (
      <div ref={ref} className={style.menuRoot}>
        {isResponsiveMenuDisplayed && (
          <div className={style.menuOverlay}>
            <div className={style.menuPanel}>
              <div className={style.svgContainer}>
                <CloseMenu
                  className={style.closeSvg}
                  title="Fermer"
                  onClick={() => setIsResponsiveMenuDisplayed(false)}
                />
              </div>

              <div className={style.menuLinkContainer}>
                {!isUserConnected && (
                  <LinkMenu className={style.menuItem}>
                    <Link href="/login" passHref className={style.menuItemLink}>
                      <LoginIcon
                        className={style.loginIcon}
                        title={t("navbar.button.login", "Login")}
                      />
                      <span className={style.menuItemLabel}>
                        {t("navbar.button.login", "Login")}
                      </span>
                    </Link>
                  </LinkMenu>
                )}
                {isUserConnected && (
                  <div
                    className={`${style.menuItem} ${style.userMenuItem} clickable noselect`}
                  >
                    <div
                      className={style.userMenuTrigger}
                      onClick={handleDisplayUserMenu}
                    >
                      <p className={style.userName}>{me?.firstName}</p>
                      <div className={style.userMenuChevron}>
                        <OutlineIcon />
                      </div>
                    </div>
                    {isUserMenuDisplayed && (
                      <div className={style.userMenuInline}>
                        <UserMenu />
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={style.menuItem}
                  onClick={() => setIsResponsiveMenuDisplayed(false)}
                >
                  <Link href="/demo" passHref className={style.menuItemLink}>
                    <FormattedMessage
                      id="navbar.button.demo"
                      defaultMessage="Demo"
                    />
                  </Link>
                </div>
                <LinkMenu className={style.menuItem}>
                  <Link
                    href={calendlyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={style.menuItemLink}
                  >
                    <FormattedMessage
                      id="generic.contactUs.calendly"
                      defaultMessage="Book a call"
                    />
                  </Link>
                </LinkMenu>
                <LinkMenu className={style.menuItem}>
                  <Link
                    href="mailto:yoann@chatsystem.ai"
                    target="_blank"
                    rel="noreferrer"
                    className={style.menuItemLink}
                  >
                    <FormattedMessage
                      id="footer.link.email"
                      defaultMessage="Email us"
                    />
                  </Link>
                </LinkMenu>
                <div
                  className={style.menuItem}
                  onClick={() => setIsResponsiveMenuDisplayed(false)}
                >
                  <Link href="/pricing" className={style.menuItemLink}>
                    <FormattedMessage
                      id="navbar.button.pricing"
                      defaultMessage="Pricing"
                    />
                  </Link>
                </div>
                <div className={style.langChoiceSlot}>
                  <AppLangChoice />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default ResponsiveMenu;
